import type { Quote } from "../../services/pulseService";
import { formatPrice, formatPercent, changeColor, changeBg, marketStateLabel, marketStateDot } from "./utils";

interface Props {
  title: string;
  quotes: Quote[];
  compact?: boolean;
}

export function QuoteTable({ title, quotes, compact }: Props) {
  if (!quotes.length) {
    return (
      <div className="glass-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">{title}</h3>
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center gap-2 text-surface-500 text-xs">
            <div className="h-2 w-2 rounded-full bg-surface-600 animate-pulse" />
            Awaiting market data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3 flex items-center gap-2">
        {title}
        {quotes[0]?.market_state && (
          <span className="flex items-center gap-1.5 text-xs font-normal text-surface-400 normal-case tracking-normal">
            <span className={`w-1.5 h-1.5 rounded-full ${marketStateDot(quotes[0].market_state)} animate-pulse`} />
            {marketStateLabel(quotes[0].market_state)}
          </span>
        )}
      </h3>

      <div className="space-y-1">
        {quotes.map((q) => (
          <div
            key={q.symbol}
            className={`flex items-center justify-between py-2 px-3 rounded-xl transition-colors ${changeBg(q.change_percent)} hover:bg-surface-800/60`}
          >
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-semibold text-surface-100 truncate">
                {q.symbol.replace("^", "")}
              </span>
              {!compact && (
                <span className="text-xs text-surface-500 truncate">{q.name}</span>
              )}
            </div>
            <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
              <span className="text-sm font-mono font-semibold text-surface-100 tabular-nums">
                {formatPrice(q.price)}
              </span>
              <div className="flex flex-col items-end min-w-[72px]">
                <span className={`text-sm font-semibold tabular-nums ${changeColor(q.change_percent)}`}>
                  {formatPercent(q.change_percent)}
                </span>
                {!compact && (
                  <span className={`text-xs tabular-nums ${changeColor(q.change)}`}>
                    {q.change >= 0 ? "+" : ""}{q.change.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
